'use client';

import { createContext, SetStateAction, useState, Dispatch, useContext, useCallback, useEffect } from 'react';

import { ReportContextProps } from '@/types/report';

type ReportContextType = {
  report: ReportContextProps;
  setReport: Dispatch<SetStateAction<ReportContextProps>>;
  handleReportChange: (newReport: Partial<ReportContextProps>) => void;
  clearReport: () => boolean;
};

export const ReportContext = createContext({} as ReportContextType);

export function ReportProvider({ children }) {
  const [report, setReport] = useState<ReportContextProps>({
    id: null,
    logged: false,
    finished: false,
    userId: null,
    reportStatusId: null,
    animal: {
      animalId: null,
      conditionId: null,
      sizeId: null,
      description: ''
    },
    images: [],
    address: {
      street: '',
      number: '',
      neighborhood: '',
      zipCode: '',
      country: '',
      latitude: null,
      longitude: null,
      city: '',
      state: '',
      formattedAddress: ''
    },
    user: {
      anonymous: false,
      name: '',
      email: '',
      phone: '',
      wantContact: false
    }
  });

  const clearReport = useCallback(() => {
    setReport({
      id: null,
      logged: false,
      finished: false,
      userId: null,
      reportStatusId: null,
      animal: {
        animalId: null,
        conditionId: null,
        sizeId: null,
        description: ''
      },
      images: [],
      address: {
        street: '',
        number: '',
        neighborhood: '',
        zipCode: '',
        country: '',
        latitude: null,
        longitude: null,
        city: '',
        state: '',
        formattedAddress: ''
      },
      user: {
        anonymous: false,
        name: '',
        email: '',
        phone: '',
        wantContact: false
      }
    });

    localStorage.removeItem('report');

    return true;
  }, []);

  // console.log('report', report);

  const handleReportChange = useCallback((newReport: ReportContextProps) => {
    setReport(oldReport => ({
      ...oldReport,
      ...newReport
    }));
  }, []);

  useEffect(() => {
    const reportStorage = localStorage.getItem('report');
    if (reportStorage) {
      setReport(JSON.parse(reportStorage));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('report', JSON.stringify(report));
  }, [report]);

  return (
    <ReportContext.Provider
      value={{
        report,
        setReport,
        handleReportChange,
        clearReport
      }}
    >
      {children}
    </ReportContext.Provider>
  );
}

export const useReportContext = () => {
  const context = useContext(ReportContext);

  if (!context) {
    throw new Error('useReportContext must be used within a ReportProvider');
  }

  return context;
};
